import React, { FC, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { IconInfo } from "../../../utils/icons";
import {
  BRAND_PROJECT_STATUSES,
  BRAND_PROJECT_STATUS_STYLES,
  STATUS_PHASE_COLORS,
  StatusPhase,
} from "../helper";

const PHASE_DESCRIPTIONS: { phase?: StatusPhase; label: string; description: string; badge?: boolean }[] = [
  { phase: "inactive",    label: "Not started",      description: "This stage has not been reached yet" },
  { phase: "active",      label: "Needs attention",  description: "This stage is active and something needs to happen" },
  { phase: "in_progress", label: "In progress",      description: "Active stage, waiting on someone else to act" },
  { phase: "complete",    label: "Complete",          description: "This stage is done" },
  { phase: "error",       label: "Denied / rejected", description: "Something was denied, rejected, or canceled" },
  { badge: true,          label: "Action required (badge)", description: "You specifically need to act on this stage" },
];

const splitInColumns = <T,>(items: T[]): T[][] => [
  items.slice(0, Math.ceil(items.length / 2)),
  items.slice(Math.ceil(items.length / 2)),
];

const PHASE_COLUMNS = splitInColumns(PHASE_DESCRIPTIONS);
const STATUS_COLUMNS = splitInColumns(Object.keys(BRAND_PROJECT_STATUSES));

export const ProjectLegend: FC = () => {
  const [showLegend, setShowLegend] = useState(false);

  return (
    <>
      <div style={{ float: "right" }} onClick={() => setShowLegend(true)}>
        <IconInfo />
      </div>

      <Dialog
        open={showLegend}
        onClose={() => setShowLegend(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h5">Project Legend</Typography>
        </DialogTitle>
        <DialogContent>
          <Box className="flex flex-col gap-5">
            <Divider />
            <Box className="flex flex-col">
              <Typography variant="subtitle1" fontFamily="CircularStdBlack">Status Icons</Typography>
              <Typography variant="body2">
                Each project card shows 5 status icons: <strong>Brief, Expert, Shipping, Review,</strong> and <strong>Download</strong>. The color of each icon tells you the state of that stage from your perspective. A <strong>red badge ( ! )</strong> on an icon means you specifically need to take action.
              </Typography>
            </Box>
            <Box className="flex gap-4 ">
              {PHASE_COLUMNS.map((column, columnIdx) => (
                <Box key={`phase-column-${columnIdx}`} className="flex flex-col gap-4 w-full">
                  {column.map(({ phase, label, description, badge }) => (
                    <Box key={label} className="flex items-center gap-3">
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          backgroundColor: badge ? STATUS_PHASE_COLORS.error : STATUS_PHASE_COLORS[phase!],
                          flexShrink: 0,
                          ...(badge && {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 9,
                            fontWeight: 900,
                            color: "#fff",
                            border: "1.5px solid #ccc",
                          }),
                        }}
                      >
                        {badge ? "!" : null}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{label}</Typography>
                        <Typography variant="caption" color="text.secondary">{description}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
            <Divider />
            <Box className="flex flex-col">
              <Typography variant="subtitle1" fontFamily="CircularStdBlack">Project Statuses</Typography>
              <Typography variant="body2">
                Each project in the panel is labeled with a status to quickly convey its current phase.
              </Typography>
            </Box>
            <Box className="flex gap-4 pb-2">
              {STATUS_COLUMNS.map((column, columnIdx) => (
                <Box key={`column-${columnIdx}`} className="flex flex-col gap-4 w-full">
                  {column.map((statusKey) => (
                    <Box key={statusKey} className="flex items-center gap-3">
                      <Box
                        className="w-4 h-4 rounded-full"
                        sx={{
                          backgroundColor: BRAND_PROJECT_STATUS_STYLES[statusKey]?.color,
                          border: "1px solid #ccc",
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">
                        {BRAND_PROJECT_STATUSES[statusKey]}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
